import React, { useEffect, useState, useRef } from 'react';

// API/Components
import {
	ANALYSIS_RESULT,
	validValue,
	isValidValue,
	AnalysisResultToStateColor,
	getMeasurementAnalysis,
} from '../enums';
import ResetZoomButton from '../reset_zoom_button';
import CustomGraphLegend from '../custom_graph_legend';

// Material UI
import Grid from '@mui/material/Grid';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';

// Chart.JS
import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	BarElement,
	Legend,
	Tooltip as ChartJSTooltip,
	BarController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin from 'chartjs-plugin-annotation';
import tinycolor from "tinycolor2";

// Register Chart.JS Plugins
ChartJS.register(
	LinearScale,
	CategoryScale,
	BarElement,
	Legend,
	ChartJSTooltip,
	BarController,
	zoomPlugin,
	annotationPlugin,
);

// System Realtime Graph Component
export default function SystemRealtimeGraph(props) {

	const { systemRealtimeData, limitData, graphScales, hasValidData, loading } = props;

	const [graphData, setGraphData] = useState({});
	const [graphSettings, setGraphSettings] = useState({
		limitsEnabled: true,
		valuesVisible: false,
	});
	const [analyses, setAnalyses] = useState({
		"voltageAnalysis": null,
		"currentAnalysis": null,
	});
	const [graphOptions, setGraphOptions] = useState({});

	const chartRef = useRef(null);

	// Update chart options only when graphScales or isInDischarge changes (preserves zoom/pan on data updates)
	useEffect(() => {
		const options = createChartOptions(graphScales, limitData, true);
		setGraphOptions(options);
	}, [graphScales, limitData, hasValidData]);

	// Update annotations separately when limits toggle (preserves zoom/pan)
	useEffect(() => {
		if (!chartRef.current) { return; }

		const annotations = buildAnnotations(limitData, graphSettings.limitsEnabled);
		if (chartRef.current.options?.plugins?.annotation) {
			chartRef.current.options.plugins.annotation.annotations = annotations;
			chartRef.current.update('none'); // Update without animation or reset
		}
	}, [graphSettings.limitsEnabled, limitData]);

	// Process system realtime data and update graph
	useEffect(() => {
		if (!systemRealtimeData || !hasValidData) { return; }

		const voltage = validValue(systemRealtimeData.voltage, null);
		const current = validValue(systemRealtimeData.current, null);

		// Get analysis results for colorization
		const voltageAnalysis = getMeasurementAnalysis(systemRealtimeData?.voltage, limitData?.volt?.hi_crit, limitData?.volt?.lo_crit, null, null);
		const currentAnalysis = getMeasurementAnalysis(systemRealtimeData?.current, limitData?.current?.system, null, null, null);

		setAnalyses({ "voltageAnalysis": voltageAnalysis, "currentAnalysis": currentAnalysis });

		// Create chart datasets with analysis-based colors
		let datasets = createChartDatasets(voltage, current, voltageAnalysis, currentAnalysis);

		// Set chart data
		const data = {
			labels: ['System'],
			datasets: datasets,
		};
		setGraphData(data);

	}, [systemRealtimeData, limitData, hasValidData]);

	// Create datasets for voltage and current with analysis-based colors
	function createChartDatasets(voltage, current, voltageAnalysis, currentAnalysis) {
		// Get base colors from analysis state for the bar fill
		const voltageAnalysisColor = AnalysisResultToStateColor(voltageAnalysis?.result);
		const currentAnalysisColor = AnalysisResultToStateColor(currentAnalysis?.result);

		// Use lighter/darker borders to differentiate measurements
		const voltageBorderColor = tinycolor(voltageAnalysisColor).lighten(30).toHexString(); // Lighter border for voltage
		const currentBorderColor = tinycolor(currentAnalysisColor).darken(20).toHexString(); // Darker border for current

		return [
			{
				label: 'Voltage (V)',
				data: [voltage],
				backgroundColor: tinycolor(voltageAnalysisColor).setAlpha(0.7).toRgbString(),
				borderColor: voltageBorderColor,
				borderWidth: 3,
				yAxisID: 'y-voltage',
				barPercentage: 0.9,
				xAxisID: 'x',
			},
			{
				label: 'Current (A)',
				data: [current],
				backgroundColor: tinycolor(currentAnalysisColor).setAlpha(0.7).toRgbString(),
				borderColor: currentBorderColor,
				borderWidth: 3,
				yAxisID: 'y-current',
				barPercentage: 0.9,
				xAxisID: 'x',
			},
		];
	};

	// Build annotation objects for limit lines
	function buildAnnotations(limitData, limitsEnabled) {
		if (!limitsEnabled || !limitData) {
			return {};
		}

		const annotations = {};

		// Get critical limit colors based on analysis results
		const highCritColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS_CRITICAL);
		const lowCritColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS_CRITICAL);

		// X-coordinates to position lines over specific bars
		// With two bars at index 0, they're positioned side-by-side
		const voltageBarXMin = -0.4;
		const voltageBarXMax = -0.005;
		const currentBarXMin = 0.005;
		const currentBarXMax = 0.4;

		// Voltage High Limit - horizontal line over voltage bar only
		if (limitData?.volt?.hi_crit !== undefined && limitData?.volt?.hi_crit !== null) {
			annotations.voltageHiLimit = {
				type: 'line',
				xMin: voltageBarXMin,
				xMax: voltageBarXMax,
				xScaleID: 'x',
				yMin: limitData.volt.hi_crit,
				yMax: limitData.volt.hi_crit,
				yScaleID: 'y-voltage',
				borderColor: highCritColor,
				borderWidth: 2,
				borderDash: [5, 5],
				label: {
					content: 'Voltage High Limit',
					display: false,
				},
			};
		}

		// Voltage Low Limit - horizontal line over voltage bar only
		if (limitData?.volt?.lo_crit !== undefined && limitData?.volt?.lo_crit !== null) {
			annotations.voltageLowLimit = {
				type: 'line',
				xMin: voltageBarXMin,
				xMax: voltageBarXMax,
				xScaleID: 'x',
				yMin: limitData.volt.lo_crit,
				yMax: limitData.volt.lo_crit,
				yScaleID: 'y-voltage',
				borderColor: lowCritColor,
				borderWidth: 2,
				borderDash: [5, 5],
				label: {
					content: 'Voltage Low Limit',
					display: false,
				},
			};
		}

		// Current High Limit - horizontal line over current bar only
		if (limitData?.current?.system !== undefined && limitData?.current?.system !== null) {
			annotations.currentHiLimit = {
				type: 'line',
				xMin: currentBarXMin,
				xMax: currentBarXMax,
				xScaleID: 'x',
				yMin: Math.abs(limitData.current.system),
				yMax: Math.abs(limitData.current.system),
				yScaleID: 'y-current',
				borderColor: highCritColor,
				borderWidth: 2,
				borderDash: [5, 5],
				label: {
					content: 'Current High Limit',
					display: false,
				},
			};
		}

		return annotations;
	};

	// Create chart options configuration
	function createChartOptions(ranges, limitData, limitsEnabled) {
		const {
			voltageDisplayMax,
			voltageDisplayMin,
			voltageBoundsMax,
			voltageBoundsMin,
			voltageMinRange,
			currentDisplayMax,
			currentDisplayMin,
			currentBoundsMax,
			currentBoundsMin,
			currentMinRange,
		} = ranges;

		const annotations = buildAnnotations(limitData, limitsEnabled);
		
		return {
			maintainAspectRatio: false,
			animation: false,
			responsive: true,
			interaction: {
				mode: 'index',
				intersect: false,
			},
			scales: {
				x: {
					grid: {
						display: false,
					},
					ticks: {
						display: true,
					},
				},
				'y-voltage': {
					type: 'linear',
					position: 'left',
					max: voltageDisplayMax,
					min: voltageDisplayMin,
					title: {
						display: true,
						text: 'Voltage (V)',
						font: {
							size: 14,
							weight: 'bold',
						},
					},
					grid: {
						color: 'rgba(0, 0, 0, 0.1)',
					},
				},
				'y-current': {
					type: 'linear',
					position: 'right',
					max: currentDisplayMax,
					min: currentDisplayMin,
					title: {
						display: true,
						text: 'Current (A)',
						font: {
							size: 14,
							weight: 'bold',
						},
					},
					grid: {
						drawOnChartArea: false,
					},
				},
			},
			plugins: {
				annotation: {
					annotations: annotations,
				},
				legend: {
					display: false,
				},
				tooltip: {
					filter: function(tooltipItem) {
						// Only show tooltip items that have actual values (not null)
						return tooltipItem.parsed.y !== null && tooltipItem.parsed.y !== undefined;
					},
					callbacks: {
						label: function(context) {
							const datasetLabel = context.dataset.label || '';
							const displayValue = context.parsed.y;

							// Return measurement with colored legend box
							if (datasetLabel.includes('Voltage')) {
								return ` Voltage: ${displayValue.toFixed(1)} VDC`;
							} else if (datasetLabel.includes('Current')) {
								return ` Current: ${displayValue.toFixed(1)} A`;
							} else {
								return `${datasetLabel}: ${displayValue.toFixed(1)}`;
							}
						},
						afterLabel: function(context) {
							// Check if annotations exist to determine if limits are shown
							const hasAnnotations = context.chart?.options?.plugins?.annotation?.annotations && 
								Object.keys(context.chart.options.plugins.annotation.annotations).length > 0;
							
							if (!hasAnnotations || !limitData) {
								return [];
							}

							const lines = [];
							const datasetLabel = context.dataset.label || '';
							
							if (datasetLabel.includes('Voltage')) {
								// Show voltage limits after voltage measurement with dotted line indicators
								if (limitData?.volt?.hi_crit !== undefined && limitData?.volt?.hi_crit !== null) {
									lines.push(` Crit High: ${limitData.volt.hi_crit.toFixed(1)} VDC`);
								}
								if (limitData?.volt?.lo_crit !== undefined && limitData?.volt?.lo_crit !== null) {
									lines.push(` Crit Low: ${limitData.volt.lo_crit.toFixed(1)} VDC`);
								}
								lines.push(' ');
							} else if (datasetLabel.includes('Current')) {
								// Show current limit after current measurement with dotted line indicator
								if (limitData?.current?.system !== undefined && limitData?.current?.system !== null) {
									lines.push(` Crit High: ${Math.abs(limitData.current.system).toFixed(1)} A`);
								}
							}
							
							return lines;
						},
					},
				},
				zoom: {
					pan: {
						enabled: true,
						mode: 'y',
					},
					limits: {
						'y-voltage': {
							max: voltageBoundsMax,
							min: voltageBoundsMin,
							minRange: voltageMinRange,
						},
						'y-current': {
							max: currentBoundsMax,
							min: currentBoundsMin,
							minRange: currentMinRange,
						},
					},
					zoom: {
						mode: 'y',
						wheel: {
							enabled: true,
							speed: 0.05,
						},
						pinch: {
							enabled: true,
						},
					},
				},
			},
		};
	};

	// Textual display of system voltage and current values
	const TextValuesDisplay = (
		<div style={{
			display: 'grid',
			gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
			paddingLeft: '10px',
			paddingRight: '10px',
			borderRadius: '4px',
			marginTop: '3px',
			maxHeight: '120px',
			flexShrink: 0,
			overflowY: 'auto',
		}}>
			<div style={{ fontSize: '12px', fontWeight: 'bold', border: '1px solid #bbb', borderRadius: '4px', padding: '3px' }}>
				<div style={{ display: 'flex', justifyContent: 'space-evenly', textAlign: 'center' }}>
					<span style={{
						color: analyses?.voltageAnalysis
							? tinycolor(AnalysisResultToStateColor(analyses?.voltageAnalysis?.result)).toHexString()
							: '#666'
					}}>
						V: {isValidValue(systemRealtimeData?.voltage) ? `${systemRealtimeData?.voltage.toFixed(1)} V` : 'Unknown'}
					</span>
					<span style={{
						color: analyses?.currentAnalysis
							? tinycolor(AnalysisResultToStateColor(analyses?.currentAnalysis?.result)).toHexString()
							: '#666'
					}}>
						I: {isValidValue(systemRealtimeData?.current) ? `${systemRealtimeData?.current.toFixed(1)} A` : 'Unknown'}
					</span>
				</div>
			</div>
		</div>
	);

	const handleLimitMarkersToggle = () => {
		setGraphSettings(prev => ({
			...prev,
			limitsEnabled: !prev.limitsEnabled,
		}));
	};

	const handleValuesToggle = () => {
		setGraphSettings(prev => ({
			...prev,
			valuesVisible: !prev.valuesVisible,
		}));
	};

	const handleGraphResetZoom = () => {
		if (chartRef.current) {
			chartRef.current.resetZoom();
		}
	};

	return (
		<div
			style={{
				width: '100%',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				paddingLeft: '5px',
				paddingRight: '5px',
				paddingBottom: '3px',
				position: 'relative',
				overflow: 'hidden',
			}}
		>
			<div style={{ fontSize: '16px', fontWeight: 'bold', textAlign: 'center', flexShrink: 0 }}>
				<strong>System</strong>
			</div>
			<div style={{ 
				flex: '1 1 auto',
				width: '100%',
				minHeight: 0,
				overflow: 'hidden',
			}}>
				{ (hasValidData && graphData && graphData.datasets && graphData.datasets.length > 0) ? (
					<Chart
						ref={chartRef}
						type='bar'
						options={graphOptions}
						data={graphData}
					/>
				) : (
					<div style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						height: '100%',
						color: '#666',
					}}>
						{loading ? 'Loading...' : 'No data available'}
					</div>
				)}
			</div>

			{graphSettings.valuesVisible && TextValuesDisplay}

			<Grid container spacing={1} alignItems="center">
				<Grid item>
					<ToggleButtonGroup
						value={[]}
						onChange={() => {}}
						aria-label="graph markers shown"
						size='small'
						sx={{ padding: '2px' }}
					>
						<Tooltip title="Toggle limit markers on the graph" arrow>
							<ToggleButton
								value="limitMarkers"
								size="small"
								selected={graphSettings.limitsEnabled}
								onClick={handleLimitMarkersToggle}
								sx={{ 
									padding: '2px 8px',
									fontSize: '12px',
									minWidth: 'auto',
								}}
							>
								Limits
							</ToggleButton>
						</Tooltip>
						<Tooltip title="Toggle display of voltage and current values" arrow>
							<ToggleButton
								value="showValues"
								size="small"
								selected={graphSettings.valuesVisible}
								onClick={handleValuesToggle}
								sx={{ 
									padding: '2px 8px',
									fontSize: '12px',
									minWidth: 'auto',
								}}
							>
								Values
							</ToggleButton>
						</Tooltip>
					</ToggleButtonGroup>
				</Grid>
				<Grid item xs sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', rowGap: 0.5, columnGap: 2, padding: '4px 0' }}>
					<CustomGraphLegend datasets={graphData.datasets} />
				</Grid>
				<Grid item>
					<ResetZoomButton onClick={handleGraphResetZoom} />
				</Grid>
			</Grid>
		</div>
	);
}